// pages/Profile/ProfileLandingPage.tsx — Co Marca — estilo plataforma dashboard
import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import {
    Camera, Mail, Lock, Eye, EyeOff, RotateCcw,
    User, Shield, CheckCircle2, Home, ChevronRight,
    Phone, CreditCard, Briefcase, CalendarCheck, Clock, XCircle,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { updateUser } from '../../../services/userService';
import { updatePersonal } from '../../../services/personalService';
import { updateCliente } from '../../../services/clienteService';
import { fetchCitas, Cita } from '../../../services/citasService';

const colors = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryLight: '#8b3fa8',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');

@keyframes fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(193, 52, 139,.3); }
    50%     { box-shadow:0 0 0 5px rgba(193, 52, 139,0); }
}
@keyframes spin { to { transform:rotate(360deg); } }

.pl-page   { animation: fadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.pl-tab-btn:hover:not(.active) { color: #374151 !important; background: #f8fafc !important; }
.pl-input {
    width:100%; padding:10px 14px;
    border:1.5px solid #e5e7eb; border-radius:10px;
    font-size:.9rem; color:#374151;
    font-family:'DM Sans',sans-serif;
    outline:none; background:#fafafa;
    transition:border-color .2s, box-shadow .2s;
    box-sizing:border-box;
}
.pl-input:focus {
    border-color:${colors.primary} !important;
    box-shadow:0 0 0 3px rgba(193, 52, 139,.10) !important;
    background:#fff !important;
}
.pl-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:10px 24px; font-size:.88rem; font-weight:800;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(193, 52, 139,.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.pl-btn-primary:hover { transform:translateY(-1px); box-shadow:0 8px 22px rgba(193, 52, 139,.32); }
.pl-btn-primary:disabled { background:#e2e8f0; color:#94a3b8; box-shadow:none; cursor:not-allowed; transform:none; }
.pl-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:#f8fafc; border:1.5px solid #e2e8f0;
    color:#374151; border-radius:11px;
    padding:10px 20px; font-size:.88rem; font-weight:700;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .2s;
}
.pl-btn-ghost:hover { background:#f1f5f9; }
.pl-stat-card { transition:all .22s cubic-bezier(.16,1,.3,1); }
.pl-stat-card:hover { transform:translateY(-3px); }
.pl-avatar-wrap:hover .pl-avatar-overlay { opacity:1 !important; }
@media (max-width:900px) {
    .pl-layout { flex-direction:column !important; }
    .pl-sidebar { width:100% !important; }
}
`;

// ── Stat types ────────────────────────────────────────────────
interface Stat { value: number; label: string; icon: React.ReactNode; color: string; }

const calcStatsPersonal = (citas: Cita[], userId: number): Stat[] => {
    const reg = citas.filter(
        (c) => (c as any).usuario_registrador === userId || (c as any).usuario_registrador_id === userId
    );
    return [
        { value: reg.length, label: 'Registradas', icon: <CalendarCheck size={16}/>, color: colors.secondary },
        { value: reg.filter((c) => c.estado_ejecucion === 'Finalizada').length, label: 'Finalizadas', icon: <CheckCircle2 size={16}/>, color: colors.secondaryLight },
        { value: reg.filter((c) => c.estado_ejecucion === 'Cancelada' || c.estado === 'Cancelada').length, label: 'Canceladas', icon: <XCircle size={16}/>, color: colors.primary },
    ];
};

const calcStatsCliente = (citas: Cita[], userId: number): Stat[] => {
    const own = citas.filter(
        (c) => Number(c.cliente) === userId || (c.clientes_asociados && c.clientes_asociados.includes(userId))
    );
    return [
        { value: own.length, label: 'Total Citas', icon: <CalendarCheck size={16}/>, color: colors.secondary },
        { value: own.filter((c) => c.estado_ejecucion === 'Finalizada').length, label: 'Finalizadas', icon: <CheckCircle2 size={16}/>, color: colors.secondaryLight },
        { value: own.filter((c) => c.estado === 'Pendiente' || c.estado_ejecucion === 'PorComenzar').length, label: 'Pendientes', icon: <Clock size={16}/>, color: colors.accentDark },
    ];
};

// ── MAIN ─────────────────────────────────────────────────────
const ProfilePage = () => {
    const { user, setUser } = useAuth();

    const [activeTab, setActiveTab] = useState<'General' | 'Seguridad'>('General');

    // Imagen
    const [selectedImage, setSelectedImage] = useState<string>(user?.foto_url ?? '');
    const [selectedFile,  setSelectedFile]  = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Stats
    const blankStats: Stat[] = user?.perfil === 'personal'
        ? [
            { value:0, label:'Registradas', icon:<CalendarCheck size={16}/>, color:colors.secondary },
            { value:0, label:'Finalizadas',  icon:<CheckCircle2 size={16}/>, color:colors.secondaryLight },
            { value:0, label:'Canceladas',   icon:<XCircle size={16}/>,      color:colors.primary },
        ]
        : [
            { value:0, label:'Total Citas',  icon:<CalendarCheck size={16}/>, color:colors.secondary },
            { value:0, label:'Finalizadas',  icon:<CheckCircle2 size={16}/>, color:colors.secondaryLight },
            { value:0, label:'Pendientes',   icon:<Clock size={16}/>,         color:colors.accentDark },
        ];
    const [stats, setStats] = useState<Stat[]>(blankStats);
    const [statsLoading, setStatsLoading] = useState(true);

    // General form
    const [firstName,     setFirstName]     = useState(user?.first_name ?? '');
    const [lastName,      setLastName]      = useState(user?.last_name  ?? '');
    const [telefono,      setTelefono]      = useState(user?.telefono   ?? '');
    const [ciNit,         setCiNit]         = useState(user?.ci_nit     ?? '');
    const [cargo,         setCargo]         = useState(user?.cargo      ?? '');
    const [savingGeneral, setSavingGeneral] = useState(false);
    const [generalMsg,    setGeneralMsg]    = useState<{ type:'success'|'error'; text:string }|null>(null);

    // Seguridad form
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword,     setNewPassword]     = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent,     setShowCurrent]     = useState(false);
    const [showNew,         setShowNew]         = useState(false);
    const [showConfirm,     setShowConfirm]     = useState(false);
    const [securityMsg,     setSecurityMsg]     = useState<{ type:'success'|'error'; text:string }|null>(null);

    useEffect(() => {
        if (user?.foto_url && !selectedFile) setSelectedImage(user.foto_url);
    }, [user?.foto_url, selectedFile]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            setStatsLoading(true);
            try {
                const citas = await fetchCitas();
                setStats(user.perfil === 'personal'
                    ? calcStatsPersonal(citas, user.id)
                    : calcStatsCliente(citas, user.id));
            } catch { /* ignore */ }
            finally { setStatsLoading(false); }
        })();
    }, [user]);

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => { if (ev.target?.result) setSelectedImage(ev.target.result as string); };
        reader.readAsDataURL(file);
    };

    const handleSaveGeneral = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        setSavingGeneral(true);
        setGeneralMsg(null);
        try {
            await updateUser(user.id, firstName, lastName, user.email, true, user.user_level_id);
            let newFotoUrl: string | null = null;
            if (user.perfil === 'personal' && user.personal_id) {
                const r = await updatePersonal(user.personal_id, { cargo, telefono, foto: selectedFile });
                newFotoUrl = r.foto_url ?? null;
            } else if (user.perfil === 'cliente' && user.cliente_id) {
                const r = await updateCliente(user.cliente_id, { telefono, ci_nit: ciNit, foto: selectedFile });
                newFotoUrl = r.foto_url ?? null;
            }
            setUser((prev) => prev ? {
                ...prev, first_name: firstName, last_name: lastName,
                telefono, ci_nit: ciNit, cargo,
                ...(newFotoUrl ? { foto_url: newFotoUrl } : {}),
            } : prev);
            setSelectedFile(null);
            if (newFotoUrl) setSelectedImage(newFotoUrl);
            setGeneralMsg({ type:'success', text:'Cambios guardados correctamente.' });
        } catch {
            setGeneralMsg({ type:'error', text:'Error al guardar los cambios. Intenta de nuevo.' });
        } finally { setSavingGeneral(false); }
    };

    const handleSavePassword = (e: React.FormEvent) => {
        e.preventDefault();
        setSecurityMsg(null);
        if (!currentPassword || !newPassword || !confirmPassword)
            return setSecurityMsg({ type:'error', text:'Por favor completa todos los campos.' });
        if (newPassword !== confirmPassword)
            return setSecurityMsg({ type:'error', text:'La nueva contraseña y la confirmación no coinciden.' });
        if (newPassword.length < 8)
            return setSecurityMsg({ type:'error', text:'La contraseña debe tener al menos 8 caracteres.' });
        setSecurityMsg({ type:'success', text:'Contraseña actualizada correctamente.' });
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    };

    const passwordFields = [
        { label:'Contraseña actual',          value:currentPassword, setter:setCurrentPassword, show:showCurrent, toggle:()=>setShowCurrent(v=>!v) },
        { label:'Nueva contraseña',           value:newPassword,     setter:setNewPassword,     show:showNew,     toggle:()=>setShowNew(v=>!v)     },
        { label:'Confirmar nueva contraseña', value:confirmPassword, setter:setConfirmPassword, show:showConfirm, toggle:()=>setShowConfirm(v=>!v) },
    ];

    const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`.toUpperCase();

    return (
        <div style={{ fontFamily:"'DM Sans',sans-serif", background:'#fff', minHeight:'100vh' }}>
            <style>{css}</style>

            {/* ── PAGE HEADER ── */}
            <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px) 0' }}>
                <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'10px' }}>
                    <a href="/dashboard" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px' }}
                        onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                        onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                        <Home size={11}/> Dashboard
                    </a>
                    <ChevronRight size={10} color="#e2e8f0"/>
                    <span style={{ color:colors.primary, fontWeight:'700' }}>Mi Perfil</span>
                </nav>

                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'12px', marginBottom:'6px' }}>
                    <h1 style={{ fontSize:'clamp(1.6rem,3vw,2.1rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1 }}>
                        Mi <span style={{ color:colors.primary }}>Perfil</span>
                    </h1>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:`${colors.primary}10`, border:`1.5px solid ${colors.primary}20`, borderRadius:'100px', padding:'5px 14px', fontSize:'.72rem', fontWeight:'700', color:colors.primaryDark }}>
                        <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:colors.primary, animation:'pulseD 2s infinite', display:'inline-block' }}/>
                        {user?.perfil === 'cliente' ? 'Miembro' : 'Personal Co Marca'}
                    </span>
                </div>
                <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500', marginBottom:'10px' }}>
                    Administra tu información personal y seguridad de cuenta.
                </p>
                <div style={{ display:'flex', gap:'5px', marginBottom:'28px' }}>
                    {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c,i)=>(
                        <div key={i} style={{ height:'3px', width:i===0?'28px':'10px', borderRadius:'3px', background:c }}/>
                    ))}
                </div>
            </div>

            {/* ── LAYOUT ── */}
            <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'0 clamp(20px,4vw,40px) 60px' }}>
                <div className="pl-layout pl-page" style={{ display:'flex', gap:'24px', alignItems:'flex-start' }}>

                    {/* ══ SIDEBAR ══ */}
                    <div className="pl-sidebar" style={{ width:'260px', flexShrink:0, display:'flex', flexDirection:'column', gap:'16px' }}>

                        {/* Avatar card */}
                        <div style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', padding:'28px 20px', boxShadow:'0 2px 16px rgba(0,0,0,.05)', textAlign:'center' }}>
                            {/* Barra colores */}
                            <div style={{ display:'flex', gap:'4px', justifyContent:'center', marginBottom:'20px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'20px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>

                            {/* Avatar */}
                            <div style={{ position:'relative', display:'inline-block', marginBottom:'14px' }} className="pl-avatar-wrap">
                                {selectedImage ? (
                                    <img src={selectedImage} alt="Avatar"
                                        style={{ width:'80px', height:'80px', borderRadius:'50%', objectFit:'cover', border:`3px solid ${colors.primary}20`, display:'block' }}/>
                                ) : (
                                    <div style={{ width:'80px', height:'80px', borderRadius:'50%', background:`linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:'900', fontSize:'1.4rem', border:`3px solid ${colors.primary}20` }}>
                                        {initials || <User size={28}/>}
                                    </div>
                                )}
                                <div className="pl-avatar-overlay" onClick={()=>fileInputRef.current?.click()} style={{
                                    position:'absolute', inset:0, borderRadius:'50%', background:'rgba(0,0,0,.45)',
                                    display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
                                    opacity:0, transition:'opacity .2s',
                                }}>
                                    <Camera size={18} color="#fff"/>
                                </div>
                                <input type="file" ref={fileInputRef} onChange={handleImageChange} style={{ display:'none' }} accept="image/*"/>
                            </div>

                            {selectedFile && (
                                <p style={{ fontSize:'.68rem', color:colors.accentDark, fontWeight:'600', marginBottom:'8px' }}>
                                    Imagen seleccionada — guarda para confirmar
                                </p>
                            )}

                            <div style={{ fontWeight:'900', fontSize:'1rem', color:'#0f172a', letterSpacing:'-.01em' }}>
                                {user?.first_name} {user?.last_name}
                            </div>
                            <div style={{ fontSize:'.75rem', color:'#94a3b8', fontWeight:'500', marginTop:'3px' }}>
                                {user?.perfil === 'cliente' ? 'Cliente' : 'Personal'} · {user?.role}
                            </div>

                            {/* Email pill */}
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', justifyContent:'center', marginTop:'12px', background:`${colors.secondary}0a`, border:`1.5px solid ${colors.secondary}15`, borderRadius:'100px', padding:'5px 12px' }}>
                                <Mail size={11} color={colors.secondary}/>
                                <span style={{ fontSize:'.68rem', color:colors.secondary, fontWeight:'600', maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.email}</span>
                            </div>
                        </div>

                        {/* Stats card */}
                        <div style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', padding:'20px', boxShadow:'0 2px 16px rgba(0,0,0,.05)' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'16px' }}>
                                <div style={{ height:'2px', width:'20px', background:colors.primary, borderRadius:'2px' }}/>
                                <span style={{ fontSize:'.65rem', fontWeight:'800', color:colors.primary, textTransform:'uppercase', letterSpacing:'.1em' }}>Estadísticas</span>
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                                {stats.map(s => (
                                    <div key={s.label} className="pl-stat-card" style={{
                                        display:'flex', alignItems:'center', gap:'12px',
                                        background:`${s.color}06`, borderRadius:'12px', padding:'10px 14px',
                                        border:`1.5px solid ${s.color}14`,
                                    }}>
                                        <div style={{ width:'32px', height:'32px', borderRadius:'9px', background:`${s.color}14`, color:s.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                            {s.icon}
                                        </div>
                                        <div>
                                            <div style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', lineHeight:1 }}>
                                                {statsLoading ? '—' : s.value}
                                            </div>
                                            <div style={{ fontSize:'.68rem', color:'#94a3b8', fontWeight:'600', marginTop:'1px' }}>{s.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ══ MAIN PANEL ══ */}
                    <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 2px 16px rgba(0,0,0,.05)', overflow:'hidden' }}>

                            {/* Tabs */}
                            <div style={{ display:'flex', borderBottom:'1.5px solid #f1f5f9', padding:'0 24px' }}>
                                {(['General','Seguridad'] as const).map(tab => {
                                    const active = activeTab === tab;
                                    return (
                                        <button key={tab} onClick={()=>setActiveTab(tab)} className={`pl-tab-btn${active?' active':''}`} style={{
                                            display:'inline-flex', alignItems:'center', gap:'7px',
                                            padding:'16px 4px', marginRight:'24px',
                                            background:'transparent', border:'none', cursor:'pointer',
                                            fontFamily:"'DM Sans',sans-serif",
                                            fontSize:'.88rem', fontWeight: active?'800':'600',
                                            color: active ? colors.primary : '#94a3b8',
                                            borderBottom: active ? `2px solid ${colors.primary}` : '2px solid transparent',
                                            marginBottom:'-1.5px', transition:'all .2s',
                                        }}>
                                            {tab === 'General' ? <User size={14}/> : <Shield size={14}/>}
                                            {tab}
                                        </button>
                                    );
                                })}
                            </div>

                            <div style={{ padding:'28px 28px 32px' }}>

                                {/* ════ TAB GENERAL ════ */}
                                {activeTab === 'General' && (
                                    <form onSubmit={handleSaveGeneral} style={{ animation:'fadeUp .35s ease both' }}>
                                        <div style={{ marginBottom:'24px' }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                                                <div style={{ width:'28px', height:'28px', borderRadius:'8px', background:`${colors.primary}12`, color:colors.primary, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <User size={14}/>
                                                </div>
                                                <h2 style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>Información Personal</h2>
                                            </div>
                                            <p style={{ fontSize:'.8rem', color:'#94a3b8' }}>Actualiza tu nombre y datos de contacto.</p>
                                        </div>

                                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'20px' }}>
                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color:'#374151', marginBottom:'6px' }}>
                                                    <User size={11} color={colors.primary}/> Nombre
                                                </label>
                                                <input type="text" value={firstName} onChange={e=>setFirstName(e.target.value)} className="pl-input" placeholder="Tu nombre"/>
                                            </div>
                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color:'#374151', marginBottom:'6px' }}>
                                                    <User size={11} color={colors.primary}/> Apellido
                                                </label>
                                                <input type="text" value={lastName} onChange={e=>setLastName(e.target.value)} className="pl-input" placeholder="Tu apellido"/>
                                            </div>
                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color:'#374151', marginBottom:'6px' }}>
                                                    <Phone size={11} color={colors.secondary}/> Teléfono
                                                </label>
                                                <input type="text" value={telefono} onChange={e=>setTelefono(e.target.value)} className="pl-input" placeholder="+591 7XX XXXXX"/>
                                            </div>

                                            {user?.perfil === 'cliente' && (
                                                <div>
                                                    <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color:'#374151', marginBottom:'6px' }}>
                                                        <CreditCard size={11} color={colors.secondary}/> CI / NIT
                                                    </label>
                                                    <input type="text" value={ciNit} onChange={e=>setCiNit(e.target.value)} className="pl-input" placeholder="12345678"/>
                                                </div>
                                            )}

                                            {user?.perfil === 'personal' && (
                                                <div>
                                                    <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color:'#374151', marginBottom:'6px' }}>
                                                        <Briefcase size={11} color={colors.secondary}/> Cargo
                                                    </label>
                                                    <input type="text" value={cargo} onChange={e=>setCargo(e.target.value)} className="pl-input" placeholder="Tu cargo"/>
                                                </div>
                                            )}
                                        </div>

                                        {generalMsg && (
                                            <div style={{
                                                display:'flex', alignItems:'center', gap:'8px',
                                                background: generalMsg.type==='success' ? `${colors.secondaryLight}12` : `${colors.primary}10`,
                                                border:`1.5px solid ${generalMsg.type==='success' ? colors.secondaryLight : colors.primary}28`,
                                                borderRadius:'10px', padding:'10px 14px', marginBottom:'18px',
                                            }}>
                                                {generalMsg.type==='success' ? <CheckCircle2 size={14} color={colors.secondaryLight}/> : <XCircle size={14} color={colors.primary}/>}
                                                <span style={{ fontSize:'.8rem', fontWeight:'600', color: generalMsg.type==='success'?'#065f46':'#8f1f64' }}>
                                                    {generalMsg.text}
                                                </span>
                                            </div>
                                        )}

                                        <button type="submit" disabled={savingGeneral} className="pl-btn-primary">
                                            {savingGeneral
                                                ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block'}}/>Guardando...</>
                                                : <><CheckCircle2 size={14}/> Guardar cambios</>
                                            }
                                        </button>
                                    </form>
                                )}

                                {/* ════ TAB SEGURIDAD ════ */}
                                {activeTab === 'Seguridad' && (
                                    <div style={{ animation:'fadeUp .35s ease both' }}>

                                        {/* Correo */}
                                        <div style={{ marginBottom:'28px' }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                                                <div style={{ width:'28px', height:'28px', borderRadius:'8px', background:`${colors.secondary}12`, color:colors.secondary, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <Mail size={14}/>
                                                </div>
                                                <h2 style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>Correo Electrónico</h2>
                                            </div>
                                            <p style={{ fontSize:'.8rem', color:'#94a3b8', marginBottom:'12px' }}>
                                                Tu correo es tu identificador de acceso y no puede modificarse desde aquí.
                                            </p>
                                            <div style={{ display:'flex', alignItems:'center', gap:'10px', background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'11px', padding:'11px 16px' }}>
                                                <Mail size={14} color="#94a3b8"/>
                                                <span style={{ fontSize:'.88rem', fontWeight:'600', color:'#374151', flex:1 }}>{user?.email}</span>
                                                <span style={{ background:`${colors.secondaryLight}15`, border:`1.5px solid ${colors.secondaryLight}30`, color:'#065f46', borderRadius:'100px', padding:'2px 10px', fontSize:'.65rem', fontWeight:'800', letterSpacing:'.05em', whiteSpace:'nowrap' }}>
                                                    VERIFICADO
                                                </span>
                                            </div>
                                        </div>

                                        <div style={{ height:'1.5px', background:'#f1f5f9', marginBottom:'28px' }}/>

                                        {/* Cambiar contraseña */}
                                        <div style={{ marginBottom:'28px' }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                                                <div style={{ width:'28px', height:'28px', borderRadius:'8px', background:`${colors.primary}12`, color:colors.primary, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <Lock size={14}/>
                                                </div>
                                                <h2 style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>Cambiar Contraseña</h2>
                                            </div>
                                            <p style={{ fontSize:'.8rem', color:'#94a3b8', marginBottom:'18px' }}>
                                                Elige una contraseña segura que no uses en otros sitios.
                                            </p>

                                            <form onSubmit={handleSavePassword} style={{ maxWidth:'440px', display:'flex', flexDirection:'column', gap:'14px' }}>
                                                {passwordFields.map(({ label, value, setter, show, toggle }) => (
                                                    <div key={label}>
                                                        <label style={{ display:'block', fontSize:'.78rem', fontWeight:'700', color:'#374151', marginBottom:'6px' }}>{label}</label>
                                                        <div style={{ position:'relative' }}>
                                                            <input type={show?'text':'password'} value={value}
                                                                onChange={e=>setter(e.target.value)}
                                                                placeholder="••••••••" className="pl-input"
                                                                style={{ paddingRight:'40px' }}/>
                                                            <button type="button" onClick={toggle} style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', display:'flex', padding:0 }}>
                                                                {show ? <EyeOff size={15}/> : <Eye size={15}/>}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}

                                                {securityMsg && (
                                                    <div style={{
                                                        display:'flex', alignItems:'center', gap:'8px',
                                                        background: securityMsg.type==='success' ? `${colors.secondaryLight}12` : `${colors.primary}10`,
                                                        border:`1.5px solid ${securityMsg.type==='success' ? colors.secondaryLight : colors.primary}28`,
                                                        borderRadius:'10px', padding:'10px 14px',
                                                    }}>
                                                        {securityMsg.type==='success' ? <CheckCircle2 size={14} color={colors.secondaryLight}/> : <XCircle size={14} color={colors.primary}/>}
                                                        <span style={{ fontSize:'.8rem', fontWeight:'600', color: securityMsg.type==='success'?'#065f46':'#8f1f64' }}>
                                                            {securityMsg.text}
                                                        </span>
                                                    </div>
                                                )}

                                                <button type="submit" className="pl-btn-primary" style={{ alignSelf:'flex-start' }}>
                                                    <Lock size={13}/> Guardar contraseña
                                                </button>
                                            </form>
                                        </div>

                                        <div style={{ height:'1.5px', background:'#f1f5f9', marginBottom:'28px' }}/>

                                        {/* Recuperar contraseña */}
                                        <div style={{ background:`${colors.accentDark}06`, border:`1.5px solid ${colors.accentDark}18`, borderRadius:'16px', padding:'20px 22px', display:'flex', alignItems:'flex-start', gap:'14px' }}>
                                            <div style={{ width:'38px', height:'38px', borderRadius:'11px', background:`${colors.accentDark}14`, color:colors.accentDark, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                <RotateCcw size={16}/>
                                            </div>
                                            <div>
                                                <div style={{ fontWeight:'800', fontSize:'.9rem', color:'#0f172a', marginBottom:'4px' }}>¿Olvidaste tu contraseña?</div>
                                                <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.6, marginBottom:'12px' }}>
                                                    Si no recuerdas tu contraseña actual, podemos enviarte un enlace de recuperación a tu correo registrado.
                                                </p>
                                                <button type="button" className="pl-btn-ghost" style={{ fontSize:'.8rem' }}
                                                    onClick={()=>alert('Se enviará un enlace de recuperación a: ' + user?.email)}>
                                                    <RotateCcw size={13} color={colors.accentDark}/> Recuperar contraseña
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ProfilePage;